import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Slider,
  IconButton,
  Tooltip
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useContent } from "../context/ContentContext";
import API from "../services/API";
import NewElementUploader from "../components/elementUploads/NewElementUploader";
import AiElementGenerator from "../components/elementUploads/AiElementGenerator";
import BulkElementUploader from "../components/elementUploads/BulkElementUploader";
import WorldUnitEditor from "../components/WorldUnitEditor";
import { AppButton, AppTextField, Panel } from "../components/ui";
import "./ElementsPage.scss";

const ELEMENTS_REFRESH_INTERVAL_MS = 5000;
const getElementSortTimestamp = (element) =>
  element?.dateModified ||
  element?.lastModified ||
  element?.updatedAt ||
  element?.metadata?.dateModified ||
  element?.status?.timestamp ||
  "";

const ELEMENT_STATUS_LABELS = {
  BUILD_TRIGGERED: "Build triggered",
  QUEUED: "Queued",
  PREPROCESSING: "Preprocessing",
  PREPROCESSING_FAILED: "Preprocessing failed",
  WAITING_TO_START_BUILD: "Waiting to build",
  BUILDING: "Building",
  BUILDING_COMPLETE: "Build complete",
  BUILDING_FAILED: "Build failed",
};

const formatStatusLabel = (statusType = "") =>
  ELEMENT_STATUS_LABELS[statusType] ||
  String(statusType || "Unknown")
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatStatusTimestamp = (timestamp) => {
  if (!timestamp) return "";

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return timestamp;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const formatDuration = (totalSeconds) => {
  const seconds = Number(totalSeconds);
  if (!Number.isFinite(seconds) || seconds < 0) return null;

  const roundedSeconds = Math.round(seconds);
  const hours = Math.floor(roundedSeconds / 3600);
  const minutes = Math.floor((roundedSeconds % 3600) / 60);
  const remainingSeconds = roundedSeconds % 60;

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  if (minutes > 0) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }

  return `${remainingSeconds}s`;
};

const getEstimatedBuildSecondsLeft = (tracking = {}) => {
  const estimatedBuildSeconds = Number(tracking.estimatedBuildSeconds);
  if (!Number.isFinite(estimatedBuildSeconds) || estimatedBuildSeconds < 0) {
    return null;
  }

  const buildStartedAtMs = tracking.buildStartedAt
    ? new Date(tracking.buildStartedAt).getTime()
    : NaN;

  if (!Number.isFinite(buildStartedAtMs)) {
    return estimatedBuildSeconds;
  }

  const elapsedSeconds = Math.max(0, (Date.now() - buildStartedAtMs) / 1000);
  return Math.max(0, estimatedBuildSeconds - elapsedSeconds);
};

const getStatusDetails = (status = {}) => {
  const safeStatus = status && typeof status === "object" ? status : {};
  const tracking = safeStatus.buildTracking || {};
  const details = [];

  if (safeStatus.type === "WAITING_TO_START_BUILD") {
    if (Number.isFinite(Number(tracking.queuePosition))) {
      details.push(`Position ${tracking.queuePosition}`);
    }

    if (Number.isFinite(Number(tracking.jobsAhead))) {
      details.push(`${tracking.jobsAhead} ahead`);
    }

    const wait = formatDuration(tracking.estimatedWaitSeconds);
    if (wait) {
      details.push(`est. wait ${wait}`);
    }
  }

  if (safeStatus.type === "BUILDING") {
    const buildTimeLeft = formatDuration(getEstimatedBuildSecondsLeft(tracking));
    if (buildTimeLeft) {
      details.push(`est. left ${buildTimeLeft}`);
    }
  }

  if (safeStatus.type === "BUILDING_COMPLETE" || safeStatus.type === "BUILDING_FAILED") {
    const duration = formatDuration(tracking.buildDurationSeconds);
    if (duration) {
      details.push(`duration ${duration}`);
    }
  }

  if (safeStatus.type === "PREPROCESSING_FAILED" && safeStatus.stage) {
    details.push(safeStatus.stage.toLowerCase());
  }

  return details;
};

const renderElementBuildStatus = (status = {}, getStatusClass) => {
  const safeStatus = status && typeof status === "object" ? status : {};
  const statusType = safeStatus.type || "UNKNOWN";
  const details = getStatusDetails(safeStatus);

  return (
    <div className={`elementCardStatus ${getStatusClass(statusType)}`}>
      <div className="elementCardStatusHeader">
        <span className="elementCardStatusLabel">{formatStatusLabel(statusType)}</span>
        {safeStatus.timestamp && (
          <span className="elementCardStatusTimestamp">
            {formatStatusTimestamp(safeStatus.timestamp)}
          </span>
        )}
      </div>

      {details.length > 0 && (
        <div className="elementCardStatusMeta">{details.join(" · ")}</div>
      )}

      {safeStatus.message && (
        <div className="elementCardStatusMessage">{safeStatus.message}</div>
      )}
    </div>
  );
};

const ElementsPage = () => {
  const {
    myElements,
    elementsLoading,
    fetchElements,
    patchMyElement,
    removeMyElement,
    trackElementBuild,
  } = useContent();
  const [editingId, setEditingId] = useState(null);
  const [editedElements, setEditedElements] = useState({});

  // Sorting and delete states
  const [sortField, setSortField] = useState("dateModified");
  const [sortOrder, setSortOrder] = useState("desc");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [elementToDelete, setElementToDelete] = useState(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch elements on mount using context.
  useEffect(() => {
    fetchElements();
  }, [fetchElements]);

  useEffect(() => {
    let isActive = true;
    let timerId = null;

    const scheduleRefresh = () => {
      timerId = window.setTimeout(async () => {
        if (!isActive) return;

        if (typeof document === "undefined" || document.visibilityState !== "hidden") {
          try {
            await fetchElements(true);
          } catch (error) {
            console.warn("Error refreshing elements list:", error);
          }
        }

        scheduleRefresh();
      }, ELEMENTS_REFRESH_INTERVAL_MS);
    };

    scheduleRefresh();

    return () => {
      isActive = false;
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [fetchElements]);

  // ----- Edit Existing Element Handlers -----
  const handleEdit = async (elementId) => {
    try {
      await API.get(`elements/${elementId}/status`, {
        requiresAuth: true,
      });
      const response = await API.get(`elements/${elementId}`, {
        requiresAuth: true,
      });
      const fullElement = response?.data?.data || response?.data;
      setEditingId(elementId);
      setEditedElements((prev) => ({
        ...prev,
        [elementId]: {
          displayName: fullElement.labelData.displayName,
          description: fullElement.labelData.description,
          worldUnit: fullElement.worldUnit,
          resolution: fullElement.visualizerData.resolution,
          fullElement: fullElement,
        },
      }));
    } catch (error) {
      console.error("Error fetching full element data:", error);
    }
  };

  const handleChange = (elementId, field, value) => {
    setEditedElements((prev) => ({
      ...prev,
      [elementId]: {
        ...prev[elementId],
        [field]: value,
      },
    }));
  };

  // Helper to update nested worldUnit values.
  const handleWorldUnitChange = (elementId, field, value) => {
    setEditedElements((prev) => ({
      ...prev,
      [elementId]: {
        ...prev[elementId],
        worldUnit: {
          ...prev[elementId].worldUnit,
          [field]: value,
        },
      },
    }));
  };

  const clearEditingState = (elementId) => {
    setEditingId((currentEditingId) =>
      currentEditingId === elementId ? null : currentEditingId
    );
    setEditedElements((prev) => {
      if (!elementId || !prev[elementId]) {
        return prev;
      }

      const nextEditedElements = { ...prev };
      delete nextEditedElements[elementId];
      return nextEditedElements;
    });
  };

  const handleCancel = () => {
    clearEditingState(editingId);
  };

  const handleSave = async (elementId) => {
    const currentData = editedElements[elementId];
    if (!currentData || !currentData.fullElement) return;
    const updatedElement = {
      ...currentData.fullElement,
      labelData: {
        ...currentData.fullElement.labelData,
        displayName: currentData.displayName,
        description: currentData.description,
      },
      worldUnit: currentData.worldUnit,
      visualizerData: {
        ...currentData.fullElement.visualizerData,
        resolution: currentData.resolution,
      },
    };

    try {
      await API.post("elements/", updatedElement, { requiresAuth: true });
      patchMyElement(elementId, {
        name: updatedElement.labelData.displayName,
        description: updatedElement.labelData.description,
      });
      clearEditingState(elementId);
    } catch (error) {
      console.error("Error saving element:", error);
    }
  };

  const retriggerBuild = async (element) => {
    if (!element?.id) return;

    try {
      await API.post("build-element", element, {
        requiresAuth: true,
      });
      clearEditingState(element.id);
      trackElementBuild(element.id);
    } catch (error) {
      console.error("Error rebuilding element:", error);
    }
  };

  // ----- Delete Element Handlers -----
  const handleDeleteClick = (element) => {
    setElementToDelete(element);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!elementToDelete) return;
    try {
      await API.get(`elements/${elementToDelete.id}/delete`, {
        requiresAuth: true,
      });
      removeMyElement(elementToDelete.id);
    } catch (error) {
      console.error("Error deleting element:", error);
    } finally {
      setDeleteConfirmOpen(false);
      setElementToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setElementToDelete(null);
  };

  const elements = Array.isArray(myElements) ? myElements : [];

  const sortedElements = [...elements].sort((a, b) => {
    if (sortField === "name") {
      const aName = a.name || "";
      const bName = b.name || "";
      return sortOrder === "asc" ? aName.localeCompare(bName) : bName.localeCompare(aName);
    } else if (sortField === "dateModified") {
      const aDate = getElementSortTimestamp(a);
      const bDate = getElementSortTimestamp(b);
      const aTime = aDate ? new Date(aDate).getTime() : 0;
      const bTime = bDate ? new Date(bDate).getTime() : 0;
      return sortOrder === "asc" ? aTime - bTime : bTime - aTime;
    }
    return 0;
  });

  const getStatusClass = (status) => {
    if (status?.includes("COMPLETE")) return "complete";
    if (status?.includes("FAILED")) return "failed";
    return "pending";
  };

  if (elementsLoading && elements.length === 0) {
    return <div className="elementsPage"><div className="elementsLoading">Loading...</div></div>;
  }

  return (
    <div className="elementsPage">
      {/* Upload Button */}
      <div className="elementsUploadBtn">
        <NewElementUploader />
        <AiElementGenerator />
        <BulkElementUploader />
      </div>

      <Typography className="elementsTitle" variant="h4">
        Elements
      </Typography>

      {/* Sorting Controls */}
      <div className="elementsSortRow">
        <div className="elementsSortGroup">
          <ButtonGroup size="small" aria-label="sort elements">
            <AppButton
              kind={sortField === "dateModified" ? "primary" : "secondary"}
              onClick={() => setSortField("dateModified")}
            >
              Sort: Date Updated
            </AppButton>
            <AppButton
              kind={sortField === "name" ? "primary" : "secondary"}
              onClick={() => setSortField("name")}
            >
              Sort: Name
            </AppButton>
          </ButtonGroup>
        </div>

        <Tooltip title={sortOrder === "asc" ? "Ascending" : "Descending"}>
          <IconButton
            className="elementsSortArrow"
            size="small"
            onClick={() => setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))}
          >
            {sortOrder === "asc" ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </IconButton>
        </Tooltip>
      </div>

      {/* Elements List */}
      <div className="elementsList app-row-list">
        {sortedElements.map((element) => (
          <Panel key={element.id} className="elementCard" hoverable>
            {editingId === element.id ? (
              <div className="elementEditForm">
                <div className="elementEditFields">
                  <AppTextField
                    label="Display Name"
                    value={editedElements[element.id].displayName}
                    onChange={(e) => handleChange(element.id, "displayName", e.target.value)}
                    fullWidth
                  />
                  <AppTextField
                    label="Description"
                    value={editedElements[element.id].description}
                    onChange={(e) => handleChange(element.id, "description", e.target.value)}
                    fullWidth
                  />
                </div>

                <div className="elementEditActions">
                  <AppButton onClick={() => handleSave(element.id)}>
                    Save
                  </AppButton>
                  <AppButton kind="ghost" onClick={handleCancel}>
                    Cancel
                  </AppButton>
                  <AppButton kind="ghost" onClick={() => retriggerBuild(editedElements[element.id].fullElement)}>
                    Rebuild
                  </AppButton>
                </div>

                <div className="elementEditExtras">
                  <Box sx={{ width: 400 }}>
                    <WorldUnitEditor
                      worldUnit={editedElements[element.id].worldUnit}
                      onChange={(field, value) => handleWorldUnitChange(element.id, field, value)}
                    />
                  </Box>

                  <Box sx={{ width: 200 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Resolution
                    </Typography>
                    <Slider
                      value={editedElements[element.id].resolution}
                      onChange={(e, value) => handleChange(element.id, "resolution", value)}
                      min={0.7}
                      max={1}
                      step={0.005}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                </div>

                <Box sx={{ mt: 2 }}>
                  <NewElementUploader
                    initialElementId={element.id}
                    initialElementData={editedElements[element.id].fullElement}
                  />
                </Box>
              </div>
            ) : (
              <div className="elementCardContent">
                <div className="elementCardInfo">
                  <div className="elementCardName">{element.name || "No Name"}</div>
                  <div className="elementCardId">{element.id || "No ID"}</div>
                  {renderElementBuildStatus(element.status, getStatusClass)}
                </div>

                <div className="elementCardDesc">
                  {element.description || "No Description"}
                </div>

                <div className="elementCardActions">
                  <AppButton className="elementCardBtn" kind="secondary" onClick={() => handleEdit(element.id)}>
                    Edit
                  </AppButton>
                  <AppButton className="elementCardBtn" kind="danger" onClick={() => handleDeleteClick(element)}>
                    Delete
                  </AppButton>
                </div>
              </div>
            )}
          </Panel>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={cancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {elementToDelete ? elementToDelete.name || "this element" : "this element"}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <AppButton kind="ghost" onClick={cancelDelete}>
            Cancel
          </AppButton>
          <AppButton kind="danger" onClick={confirmDelete}>
            Delete
          </AppButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ElementsPage;
